import React from 'react';
import { Attributes } from '@leverege/ui-attributes';
import { ModelFactory } from '@leverege/ui-plugin';
import { GroupDelegate, Relationship } from '@leverege/molten-core';
import GeoPointModel from "../../dataViewers/map/googleMap/renderers/GeoPointModel.js";
import GeoShapeModel from "../../dataViewers/map/googleMap/renderers/GeoShapeModel.js";
import GroupingLoadDataRenderer from "../map/GroupingLoadDataRenderer.js";
import { GoogleMapsLayerRendererFactory } from "../map/Factory.js";
import InstallGeoJsonPointerAction from "./InstallGeoJsonPointerAction.js";
import EditGeoJsonPointerAction from "./EditGeoJsonPointerAction.js";
import LoaderLayer from "./LoaderLayer.js";
import MoveGeoPointAction from "../map/MoveGeoPointAction.js";
import { LoadDataRenderer } from "../../dataViewers/map/mapbox/renderers/index.js";
import Util from "../map/Util.js";
import { GeohashGridModel, GroupClusterModel } from "../../dataViewers/map/googleMap/renderers/index.js";
const GROUP_CLUSTERING_TAG = 'has:clustering:group';
const PROXIMITY_CLUSTERING_TAG = 'has:clustering:proximity';
function processRelationship(molten, relationship, options) {
  const {
    attribute,
    parentRelationship: pRel,
    blueprint,
    path
  } = relationship;
  const gpAttr = Attributes.getAttribute('geoPosition', blueprint.type);
  if (attribute == null && gpAttr) {
    const isGroupCluster = gpAttr?.source?.attribute?.hasTag(GROUP_CLUSTERING_TAG);
    const isProximityCluster = gpAttr?.source?.attribute?.hasTag(PROXIMITY_CLUSTERING_TAG);
    const isCluster = isGroupCluster || isProximityCluster;
    molten.addPlugin('GoogleMapsRenderer', {
      id: `blueprint.${blueprint.type}.GeoPointRenderer`,
      matches: {
        client: 'GoogleMapsDataViewer',
        objectType: blueprint.type
      },
      layerName: blueprint.type,
      sort: 'aaa',
      createModel(lastModel) {
        let modelClzz;
        let backupModelClzz;
        const customPointRenderer = blueprint.getTagStartingWith('googleMapsLayerRenderer:', true);
        const layerRendererType = isGroupCluster ? GroupClusterModel.TYPE : isProximityCluster ? GeohashGridModel.TYPE : customPointRenderer || GeoPointModel.TYPE;
        const backupLayerRendererType = isCluster ? customPointRenderer || GeoPointModel.TYPE : null;
        if (layerRendererType) {
          modelClzz = ModelFactory.get(layerRendererType);
        }
        if (backupLayerRendererType) {
          backupModelClzz = ModelFactory.get(backupLayerRendererType);
        }
        if (!modelClzz) {
          modelClzz = GeoPointModel;
        }
        const modelName = `${blueprint.namePlural} ${gpAttr.displayName}`;
        let backupRenderer;
        if (isCluster) {
          if (lastModel && !isGeoClusterModel(lastModel)) {
            backupRenderer = {
              ...lastModel,
              visible: true
            };
          } else if (backupModelClzz) {
            backupRenderer = backupModelClzz.create(blueprint.type, modelName, blueprint.type, {
              visible: true
            });
          } else {
            backupRenderer = GeoPointModel.create(blueprint.type, modelName, blueprint.type, {
              visible: true
            });
          }
        }
        if (lastModel == null || lastModel.objectType !== blueprint.type || lastModel.type !== modelClzz.TYPE) {
          return modelClzz.create(blueprint.type, modelName, blueprint.type, {
            visible: true,
            attrName: gpAttr.name,
            backupRenderer
          });
        }
        if (isCluster) {
          return typeof modelClzz.update === 'function' ? modelClzz.update(lastModel, {
            visible: true,
            attribute: gpAttr,
            blueprint,
            modelName,
            backupRenderer
          }) : lastModel;
        }
        return typeof modelClzz.update === 'function' ? modelClzz.update(lastModel, {
          attribute: gpAttr,
          blueprint,
          modelName
        }) : lastModel;
      },
      create(props) {
        const {
          relationship,
          model
        } = props;
        const isItem = relationship.isItem || relationship.isOneToOne;
        const renderer = isItem && model?.backupRenderer ? GoogleMapsLayerRendererFactory.get(model.backupRenderer) : GoogleMapsLayerRendererFactory.get(model);
        const backupRenderer = isItem ? null : GoogleMapsLayerRendererFactory.get(model?.backupRenderer || GeoPointModel.TYPE);
        let m = isItem && model?.backupRenderer ? model.backupRenderer : model;
        m = m ? {
          ...m,
          visible: model?.visible
        } : m;
        return /*#__PURE__*/React.createElement(GroupingLoadDataRenderer, {
          ...props,
          model: m,
          renderer,
          backupRenderer,
          attributeName: gpAttr.name,
          attribute: gpAttr,
          key: relationship.path
        });
      }
    });
    if (gpAttr.blueprint.editable) {
      molten.addPlugin('Action', MoveGeoPointAction.create({
        relationship,
        attribute: gpAttr.blueprint.attribute
      }));
    }
  }
  if (attribute == null) {
    // check for shapes
    // const shapes = blueprint.attributes.filter( attr => attr.type === 'geoJson' || attr.type === 'geoPoint' )
    const shapes = Attributes.getAttributesFor(blueprint.type)
    // .map( attr => attr.blueprint?.attribute )
    .filter(attr => {
      return attr.valueType === 'geoJson' || attr.valueType === 'geoPoint';
    });
    shapes.forEach(uiAttr => {
      if (uiAttr.blueprint?.attribute?.id === gpAttr?.blueprint?.attribute?.id) {
        return;
      }
      const isGroupCluster = uiAttr?.source?.attribute?.hasTag(GROUP_CLUSTERING_TAG);
      const isProximityCluster = uiAttr?.source?.attribute?.hasTag(PROXIMITY_CLUSTERING_TAG);
      const isCluster = isGroupCluster || isProximityCluster;
      const attr = uiAttr.blueprint.attribute;
      const path = attr.path.replace(/\//g, '.');
      const layerName = `${blueprint.type}.${path}`;
      molten.addPlugin('GoogleMapsRenderer', {
        id: `blueprint.${blueprint.type}.${path}.google.GeoShapeRenderer`,
        matches: {
          client: 'GoogleMapsDataViewer',
          objectType: blueprint.type
        },
        layerName,
        sort: `zzzzz.${attr.name}`,
        // put at end
        createModel(lastModel) {
          let modelClzz;
          let backupModelClzz;
          const customPointRenderer = blueprint.getTagStartingWith('googleMapsLayerRenderer:', true);
          const layerRendererType = isGroupCluster ? GroupClusterModel.TYPE : isProximityCluster ? GeohashGridModel.TYPE : customPointRenderer || attr.type === 'geoJson' ? GeoShapeModel.TYPE : GeoPointModel.TYPE;
          const backupLayerRendererType = isCluster ? customPointRenderer || attr.type === 'geoJson' ? GeoShapeModel.TYPE : GeoPointModel.TYPE : null;
          if (layerRendererType) {
            modelClzz = ModelFactory.get(layerRendererType);
          }
          if (backupLayerRendererType) {
            backupModelClzz = ModelFactory.get(backupLayerRendererType);
          }
          if (!modelClzz) {
            modelClzz = attr.type === 'geoJson' ? GeoShapeModel : GeoPointModel;
          }
          const modelName = `${blueprint.namePlural} ${attr.name}`;
          let backupRenderer;
          if (isCluster) {
            if (lastModel && !isGeoClusterModel(lastModel)) {
              backupRenderer = {
                ...lastModel,
                visible: true
              };
            } else if (backupModelClzz) {
              backupRenderer = backupModelClzz.create(blueprint.type, modelName, blueprint.type, {
                visible: true
              });
            } else {
              backupRenderer = GeoPointModel.create(blueprint.type, modelName, blueprint.type, {
                visible: true
              });
            }
          }
          if (lastModel == null || lastModel.objectType !== blueprint.type || lastModel.type !== modelClzz.TYPE) {
            return modelClzz.create(layerName, modelName, blueprint.type, {
              attrName: uiAttr.name,
              backupRenderer
            });
          }
          if (isCluster) {
            return typeof modelClzz.update === 'function' ? modelClzz.update(lastModel, {
              attribute: gpAttr,
              blueprint,
              modelName,
              backupRenderer
            }) : lastModel;
          }
          return typeof modelClzz.update === 'function' ? modelClzz.update(lastModel, {
            attribute: uiAttr,
            blueprint,
            modelName
          }) : lastModel;
        },
        create: props => {
          const {
            model,
            relationship
          } = props;
          const isItem = relationship.isItem || relationship.isOneToOne;
          const renderer = isItem && model?.backupRenderer ? GoogleMapsLayerRendererFactory.get(model.backupRenderer) : GoogleMapsLayerRendererFactory.get(model);
          const backupRenderer = isItem ? null : GoogleMapsLayerRendererFactory.get(model?.backupRenderer || attr.type === 'geoJson' ? GeoShapeModel.TYPE : GeoPointModel.TYPE);
          let m = isItem && model?.backupRenderer ? model.backupRenderer : model;
          m = m ? {
            ...m,
            visible: model?.visible
          } : m;
          return /*#__PURE__*/React.createElement(GroupingLoadDataRenderer, {
            ...props,
            model: m,
            renderer,
            backupRenderer,
            attributeName: uiAttr.name,
            attribute: uiAttr,
            key: layerName
          });
        }
      });
      if (uiAttr.valueType === 'geoJson') {
        const name = shapes.length === 1 ? null : `${blueprint.name} ${attr.name}`;
        molten.addPlugin('Action', {
          ...InstallGeoJsonPointerAction.create({
            relationship,
            attribute: attr,
            name
          }),
          id: `blueprint.${blueprint.type}.${path}.google.CreatePointer`,
          layout: {
            group: ['Settings']
          },
          matches: {
            client: 'GoogleMapsDataViewer',
            objectType: blueprint.type,
            use: 'bottomRightActionBar'
          }
        });
        molten.addPlugin('Action', {
          ...EditGeoJsonPointerAction.create({
            relationship,
            attribute: attr
          }),
          id: `blueprint.${blueprint.type}.${path}.google.EditPointer`,
          matches: {
            client: 'GoogleMapsRolloverSelectionPointer',
            use: 'contextMenu'
          }
        });
      }
    });
    addBlueprintMapLayers(molten, blueprint);
  }
  if (attribute) {
    // Show these objects in its siblings' views. This means adding layers at paths for 
    // this attributes' containing/parent blueprint's attributes
    const siblings = attribute.blueprint.attributes.filter(a => a != attribute && a.isRelationship()) // eslint-disable-line eqeqeq
    .map(a => `${pRel.path}.${a.path}`);

    // What about grand-aunts/uncles
    if (siblings.length > 0) {
      const nShapes = Attributes.getAttributesFor(blueprint.type).filter(attr => attr.valueType === 'geoJson' || attr.valueType === 'geoPoint');
      // const shapes = blueprint.attributes.filter( attr => attr.type === 'geoJson' || attr.type === 'geoPoint' )
      nShapes.forEach(uiAttr => {
        // if ( uiAttr.blueprint?.attribute?.id === gpAttr?.blueprint?.attribute?.id ) {
        //   return
        // }
        const shapeAttr = uiAttr.blueprint.attribute;
        const isGroupCluster = gpAttr?.source?.attribute?.hasTag(GROUP_CLUSTERING_TAG);
        const isProximityCluster = gpAttr?.source?.attribute?.hasTag(PROXIMITY_CLUSTERING_TAG);
        const isCluster = isGroupCluster || isProximityCluster;
        const model = shapeAttr.type === 'geoJson' ? GeoShapeModel : isGroupCluster ? GroupClusterModel : isProximityCluster ? GeohashGridModel : GeoPointModel;
        const backupModel = isCluster ? shapeAttr.type === 'geoJson' ? GeoShapeModel : GeoPointModel : undefined;
        const renderer = GoogleMapsLayerRendererFactory.get(model.TYPE);
        const backupRenderer = GoogleMapsLayerRendererFactory.get(backupModel?.TYPE);
        const shapePath = shapeAttr.path.replace(/\//g, '.');
        const layerName = `${path}.${shapePath}`;
        const modelName = nShapes.length === 1 ? `${blueprint.namePlural} ${shapeAttr.name}` : `${pRel.blueprint.name} ${attribute.name} ${shapeAttr.name}`;
        molten.addPlugin('GoogleMapsRenderer', {
          id: `blueprint.${layerName}.${path}.google.GeoShapeRenderer`,
          matches: {
            client: 'GoogleMapsDataViewer',
            path: (all, val) => {
              // works for all siblings or ancestors
              return val !== path && val?.length > pRel.path.length && val?.startsWith(pRel.path) || siblings.indexOf(val) >= 0;
            }
          },
          layerName,
          sort: 'zzz',
          createModel: Util.newModelCreator(model, {
            objectType: blueprint.type,
            layerName,
            name: modelName
          }, {
            attrName: uiAttr.name,
            backupRenderer: backupModel?.create(layerName, modelName, blueprint.type, {
              visible: true
            })
          }),
          create: props => {
            const {
              relationship,
              delegate
            } = props;
            const ancestor = Relationship.getByPath(pRel.path);
            if (ancestor == null) {
              // eslint-disable-next-line no-console
              console.warn('Expected to find ancestor relationship but didnt', pRel.path, relationship);
              return null;
            }
            let sRel = Relationship.getByPath(path); // createSubRelationship( ancestor, attribute )
            sRel = sRel.createResolved(delegate.match);
            // const ancestorActions = new ItemDelegate( { match : delegate.match, relationship : ancestor } )
            const ancestorActions = new GroupDelegate({
              match: delegate.match,
              relationship: sRel
            });
            return /*#__PURE__*/React.createElement(LoadDataRenderer, {
              ...props,
              renderer,
              // : GeoShapeRenderer, 
              backupRenderer,
              delegate: ancestorActions,
              key: layerName,
              relationship: sRel,
              attributeName: uiAttr.name,
              attribute: uiAttr
            });
          }
        });
      });
    }
  }
}
function addBlueprintMapLayers(molten, blueprint) {
  const meta = blueprint.metadata;
  const mapLayers = meta?.mapLayers;
  if (mapLayers) {
    Object.keys(mapLayers).forEach(layerKey => {
      const layer = mapLayers[layerKey];
      molten.addPlugin('GoogleMapsRenderer', LoaderLayer.create({
        blueprint,
        layer,
        layerKey
      }));
    });
  }
}
function isGeoClusterModel(model) {
  return model?.type === GeohashGridModel.TYPE || model?.type === GroupClusterModel.TYPE;
}
export default {
  id: 'molten.BlueprintGoogleMapsRendererCreator',
  processRelationship
};